import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Banner,
  Box,
  Spinner,
  Badge,
  List,
  Divider,
  Icon,
  SkeletonBodyText,
} from '@shopify/polaris';
import {
  CheckCircleIcon,
  ClockIcon,
  ChartVerticalFilledIcon,
  ViewIcon,
  PlayIcon,
  ChatIcon,
  ProductIcon,
  EmailIcon,
  DiscountIcon,
  CartIcon,
  TargetIcon,
  AdjustIcon,
} from '@shopify/polaris-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { useOperationProgressContext } from '../context/OperationProgressContext';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReviewPromptBanner from '../components/ReviewPromptBanner';
import { useAuthenticatedFetch } from '../hooks/useApi';

export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { shop, shopInfo, settings, analytics, setupStatus, themeStatus, isLoading, isLoadingAnalytics, isLoadingSetupStatus, isLoadingThemeStatus, error, refreshSetupStatus, refreshShopInfo } = useShop();
  const { startOperation } = useOperationProgressContext();
  const fetchWithAuth = useAuthenticatedFetch();
  const [searchParams, setSearchParams] = useSearchParams();

  // Attribution metrics (revenue, AOV)
  const [attrRevenue, setAttrRevenue] = useState<number>(0);
  const [attrAOV, setAttrAOV] = useState<number>(0);
  const [, setAttrConversions] = useState<number>(0);

  // Fetch attribution data for dashboard revenue badges
  useEffect(() => {
    if (!shop) return;
    (async () => {
      try {
        const data = await fetchWithAuth<{ totalRevenue?: number; aov?: number; totalConversions?: number }>('/api/attribution/overview?days=30');
        if (data) {
          setAttrRevenue(data.totalRevenue ?? 0);
          setAttrAOV(data.aov ?? 0);
          setAttrConversions(data.totalConversions ?? 0);
        }
      } catch {}
    })();
  }, [shop]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle billing redirect from Shopify
  useEffect(() => {
    const billing = searchParams.get('billing');
    if (billing === 'success') {
      setTestMessage(t('pricingPage.billingSuccess'));
      refreshShopInfo();
      // Clean up URL params
      searchParams.delete('billing');
      setSearchParams(searchParams, { replace: true });
    } else if (billing === 'failed') {
      setTestError(t('pricingPage.billingFailed'));
      searchParams.delete('billing');
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Testing state
  const [isTraining, setIsTraining] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);
  const [isAnalyzingOrders, setIsAnalyzingOrders] = useState(false);
  const [testMessage, setTestMessage] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [showFullInstructions, setShowFullInstructions] = useState(false);

  // Helper to make API calls (with Bearer token authentication)
  const apiCall = async (endpoint: string, method: 'GET' | 'POST' = 'POST', body?: object) => {
    const { apiFetch } = await import('../hooks/useApi');
    const response = await apiFetch(endpoint, {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await response.json();
    return { ok: response.ok, data };
  };

  // Test functions
  const handleTrainModel = async () => {
    setIsTraining(true);
    setTestMessage(null);
    setTestError(null);
    try {
      const { ok, data } = await apiCall(`/api/testing/train-model`);
      if (ok) {
        if (data.correlationId) {
          startOperation(data.correlationId, 'TrainModel', data.message);
          setTestMessage(t('dashboard.testingTraining.trainingStarted'));
        } else {
          setTestMessage(`${t('dashboard.testingTraining.modelTrained')} ${data.message || t('dashboard.testingTraining.readyToGenerate')}`);
          refreshSetupStatus();
        }
      } else {
        setTestError(data.error || t('dashboard.testingTraining.trainError'));
      }
    } catch {
      setTestError(t('dashboard.testingTraining.connectionError'));
    }
    setIsTraining(false);
  };

  const handleSimulateBehavior = async () => {
    setIsSimulating(true);
    setTestMessage(null);
    setTestError(null);
    try {
      // Note: endpoint is simulate-behaviors (plural) and needs body
      const { ok, data } = await apiCall(`/api/testing/simulate-behaviors`, 'POST', { count: 500 });
      if (ok) {
        if (data.correlationId) {
          startOperation(data.correlationId, 'SimulateBehaviors', data.message);
          setTestMessage(t('dashboard.testingTraining.simulationStarted'));
        } else {
          setTestMessage(t('dashboard.testingTraining.simulationResult', { count: data.totalEvents || data.behaviorsCreated || 0 }));
          refreshSetupStatus();
        }
      } else {
        setTestError(data.error || t('dashboard.testingTraining.simulateError'));
      }
    } catch {
      setTestError(t('dashboard.testingTraining.connectionError'));
    }
    setIsSimulating(false);
  };

  const handleAnalyzeOrders = async () => {
    setIsAnalyzingOrders(true);
    setTestMessage(null);
    setTestError(null);
    try {
      // Needs body with shop
      const { ok, data } = await apiCall(`/api/frequently-bought/analyze`, 'POST', { maxOrders: 250 });
      if (ok) {
        if (data.correlationId) {
          startOperation(data.correlationId, 'AnalyzeOrders', data.message);
          setTestMessage(t('dashboard.testingTraining.analysisStarted'));
        } else {
          setTestMessage(t('dashboard.testingTraining.analysisResult', { orders: data.ordersAnalyzed || 0, associations: data.associationsCreated || 0 }));
          refreshSetupStatus();
        }
      } else {
        setTestError(data.error || t('dashboard.testingTraining.analyzeError'));
      }
    } catch {
      setTestError(t('dashboard.testingTraining.connectionError'));
    }
    setIsAnalyzingOrders(false);
  };

  if (!shop) {
    return (
      <Page>
        <Banner tone="warning" title={t('dashboard.errors.shopNotFound')}>
          <p>{t('dashboard.errors.shopNotFoundDesc')}</p>
        </Banner>
      </Page>
    );
  }

  if (isLoading) {
    return (
      <Page>
        <Box padding="800">
          <BlockStack gap="400" align="center">
            <Spinner size="large" />
            <Text as="p">{t('common.loading')}</Text>
          </BlockStack>
        </Box>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <Banner tone="critical" title={t('dashboard.errors.loadingError')}>
          <p>{error}</p>
        </Banner>
      </Page>
    );
  }

  const emailsEnabled = Boolean(settings?.emailSettings?.abandonedCartEnabled || settings?.emailSettings?.postPurchaseEnabled);
  const chatWidgetEnabled = Boolean(settings?.chatWidgetEnabled);

  // Setup status - richer checks for each step
  const hasBehaviorData = setupStatus?.hasBehaviorData ?? false;
  const hasAssociations = setupStatus?.hasProductAssociations ?? false;
  const isSetupComplete = setupStatus?.isComplete ?? false;

  // Step completion:
  // Step 1: Install app = always true if they can see this page
  // Step 1.5: App embed enabled = hasBehaviorData (tracking is collecting data)
  // Step 2: Theme block added = hasBehaviorData (same signal - widget fires events)
  // For display purposes we combine 1.5 and 2 into a single "tracking active" status
  const trackingActive = hasBehaviorData;
  const hasRecommendationsBlock = themeStatus?.hasRecommendationsBlock ?? false;
  const hasBundlesBlock = themeStatus?.hasBundlesBlock ?? false;
  const cartDrawerEnabled = Boolean(settings?.cartDrawerSettings?.enabled);

  return (
    <Page title={t('dashboard.title')}>
      <Layout>
        {/* Welcome Banner */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingLg">{t('dashboard.welcome')}</Text>
                  <Text as="p" tone="subdued">
                    {t('dashboard.welcomeSubtitle')}
                  </Text>
                </BlockStack>
                {isLoadingSetupStatus ? (
                  <SkeletonBodyText lines={1} />
                ) : (
                  <Badge tone={isSetupComplete ? 'success' : 'attention'}>
                    {isSetupComplete ? t('dashboard.status.active') : t('dashboard.status.setupRequired')}
                  </Badge>
                )}
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Review Prompt */}
        <Layout.Section>
          <ReviewPromptBanner />
        </Layout.Section>

        {/* Quick Setup Instructions */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="200" blockAlign="center">
                  <Text as="h2" variant="headingMd">{t('dashboard.setupInstructions.title')}</Text>
                  <Badge tone="info">{t('dashboard.setupInstructions.badge')}</Badge>
                </InlineStack>
                <Button
                  variant="plain"
                  onClick={() => setShowFullInstructions(!showFullInstructions)}
                >
                  {showFullInstructions ? t('dashboard.setupInstructions.collapse') : t('dashboard.setupInstructions.expand')}
                </Button>
              </InlineStack>
              {showFullInstructions && (
                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <Text as="p" variant="bodySm">
                    {t('dashboard.setupInstructions.content').split('\n').map((line: string, i: number) => (
                      <span key={i}>
                        {line}
                        <br />
                      </span>
                    ))}
                  </Text>
                </Box>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Setup Guide */}
        <Layout.Section>
          <Card>
            <BlockStack gap="500">
              <Text as="h2" variant="headingMd">{t('dashboard.setup.title')}</Text>

              <BlockStack gap="400">
                {/* Step 1 - Install */}
                <InlineStack gap="300" blockAlign="start">
                  <Box>
                    <Icon source={CheckCircleIcon} tone="success" />
                  </Box>
                  <BlockStack gap="100">
                    <Text as="p" fontWeight="semibold">{t('dashboard.setup.step1.title')}</Text>
                    <Text as="p" tone="subdued">{t('dashboard.setup.step1.description')}</Text>
                  </BlockStack>
                </InlineStack>

                <Divider />

                {/* Step 2 - Enable tracking App Embed */}
                <InlineStack gap="300" blockAlign="start">
                  <Box>
                    <Icon source={trackingActive ? CheckCircleIcon : ClockIcon} tone={trackingActive ? 'success' : 'warning'} />
                  </Box>
                  <BlockStack gap="200">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="p" fontWeight="semibold">{t('dashboard.step1_5.title')}</Text>
                      {trackingActive && <Badge tone="success">{t('dashboard.step1_5.trackingActive')}</Badge>}
                    </InlineStack>
                    {trackingActive ? (
                      <Text as="p" tone="subdued">
                        ✓ {t('dashboard.step1_5.trackingActive')}
                      </Text>
                    ) : (
                      <>
                        <Text as="p" tone="subdued">
                          {t('dashboard.step1_5.description')}
                        </Text>
                        <Banner tone="warning">
                          <Text as="p" variant="bodySm">{t('dashboard.step1_5.important')}</Text>
                        </Banner>
                        <Box paddingBlockStart="200">
                          <BlockStack gap="200">
                            <Text as="p" variant="bodySm" fontWeight="medium">{t('dashboard.step1_5.howTo')}</Text>
                            <List type="number">
                              <List.Item>{t('dashboard.step1_5.step1')}</List.Item>
                              <List.Item>{t('dashboard.step1_5.step2')}</List.Item>
                              <List.Item>{t('dashboard.step1_5.step3')}</List.Item>
                              <List.Item>{t('dashboard.step1_5.step4')}</List.Item>
                            </List>
                          </BlockStack>
                        </Box>
                        <Box paddingBlockStart="200">
                          <Button
                            url={`https://${shop}/admin/themes/current/editor?context=apps`}
                            external
                            variant="primary"
                          >
                            {t('dashboard.step1_5.openEditor')}
                          </Button>
                        </Box>
                      </>
                    )}
                  </BlockStack>
                </InlineStack>

                <Divider />

                {/* Step 3 (Optional) - Add product recommendations to theme */}
                <InlineStack gap="300" blockAlign="start">
                  <Box>
                    {isLoadingThemeStatus ? (
                      <Spinner size="small" />
                    ) : (
                      <Icon source={hasRecommendationsBlock ? CheckCircleIcon : ProductIcon} tone={hasRecommendationsBlock ? 'success' : 'base'} />
                    )}
                  </Box>
                  <BlockStack gap="200">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="p" fontWeight="semibold">{t('dashboard.step2.title')}</Text>
                      {isLoadingThemeStatus ? null : hasRecommendationsBlock && <Badge tone="success">{t('dashboard.step2.configured')}</Badge>}
                    </InlineStack>
                    {hasRecommendationsBlock ? (
                      <Text as="p" tone="subdued">
                        ✓ {t('dashboard.step2.addBlock')}
                      </Text>
                    ) : (
                      <>
                        <Text as="p" tone="subdued">
                          {t('dashboard.step2.addBlock')}
                        </Text>
                        <Box paddingBlockStart="200">
                          <BlockStack gap="200">
                            <Text as="p" variant="bodySm" fontWeight="medium">{t('dashboard.step2.howTo')}</Text>
                            <List type="number">
                              <List.Item>{t('dashboard.step2.step1')}</List.Item>
                              <List.Item>{t('dashboard.step2.step2')}</List.Item>
                              <List.Item>{t('dashboard.step2.step3')}</List.Item>
                              <List.Item>{t('dashboard.step2.step4')}</List.Item>
                              <List.Item>{t('dashboard.step2.step5')}</List.Item>
                              <List.Item>{t('dashboard.step2.step6')}</List.Item>
                            </List>
                          </BlockStack>
                        </Box>
                        <Box paddingBlockStart="200">
                          <Button
                            url={`https://${shop}/admin/themes/current/editor`}
                            external
                          >
                            {t('dashboard.step2.openEditor')}
                          </Button>
                        </Box>
                      </>
                    )}
                  </BlockStack>
                </InlineStack>

                <Divider />

                {/* Step 4 (Optional) - Add bundles to theme */}
                <InlineStack gap="300" blockAlign="start">
                  <Box>
                    {isLoadingThemeStatus ? (
                      <Spinner size="small" />
                    ) : (
                      <Icon source={hasBundlesBlock ? CheckCircleIcon : DiscountIcon} tone={hasBundlesBlock ? 'success' : 'base'} />
                    )}
                  </Box>
                  <BlockStack gap="200">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="p" fontWeight="semibold">{t('dashboard.step4.title')}</Text>
                      {isLoadingThemeStatus ? null : hasBundlesBlock && <Badge tone="success">{t('dashboard.step4.configured')}</Badge>}
                    </InlineStack>
                    {hasBundlesBlock ? (
                      <Text as="p" tone="subdued">
                        ✓ {t('dashboard.step4.description')}
                      </Text>
                    ) : (
                      <>
                        <Text as="p" tone="subdued">
                          {t('dashboard.step4.description')}
                        </Text>
                        <Box paddingBlockStart="200">
                          <BlockStack gap="200">
                            <Text as="p" variant="bodySm" fontWeight="medium">{t('dashboard.step4.howTo')}</Text>
                            <List type="number">
                              <List.Item>{t('dashboard.step4.step1')}</List.Item>
                              <List.Item>{t('dashboard.step4.step2')}</List.Item>
                              <List.Item>{t('dashboard.step4.step3')}</List.Item>
                              <List.Item>{t('dashboard.step4.step4')}</List.Item>
                              <List.Item>{t('dashboard.step4.step5')}</List.Item>
                              <List.Item>{t('dashboard.step4.step6')}</List.Item>
                            </List>
                          </BlockStack>
                        </Box>
                        <Box paddingBlockStart="200">
                          <Button
                            url={`https://${shop}/admin/themes/current/editor`}
                            external
                          >
                            {t('dashboard.step4.openEditor')}
                          </Button>
                        </Box>
                      </>
                    )}
                  </BlockStack>
                </InlineStack>

                <Divider />

                {/* Step 5 (Optional) - AI Chat Assistant */}
                <InlineStack gap="300" blockAlign="start">
                  <Box>
                    <Icon source={chatWidgetEnabled ? CheckCircleIcon : ChatIcon} tone={chatWidgetEnabled ? 'success' : 'base'} />
                  </Box>
                  <BlockStack gap="200">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="p" fontWeight="semibold">{t('dashboard.step5.title')}</Text>
                      {chatWidgetEnabled && <Badge tone="success">{t('dashboard.step5.configured')}</Badge>}
                    </InlineStack>
                    {chatWidgetEnabled ? (
                      <>
                        <Text as="p" tone="subdued">
                          ✓ {t('dashboard.step5.configured')}
                        </Text>
                        <Box paddingBlockStart="100">
                          <Text as="p" variant="bodySm" tone="subdued">
                            {t('dashboard.step5.settingsNote')}{' '}
                            <Button variant="plain" onClick={() => navigate('/app/chat')}>
                              {t('dashboard.step5.chatSettings')}
                            </Button>
                          </Text>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Text as="p" tone="subdued">
                          {t('dashboard.step5.description')}
                        </Text>
                        <Box paddingBlockStart="100">
                          <BlockStack gap="100">
                            <Text as="p" variant="bodySm" fontWeight="semibold">{t('dashboard.step5.howTo')}</Text>
                            <List type="number">
                              <List.Item>{t('dashboard.step5.step1')}</List.Item>
                              <List.Item>{t('dashboard.step5.step2')}</List.Item>
                            </List>
                          </BlockStack>
                        </Box>
                        <Box paddingBlockStart="200">
                          <Button onClick={() => navigate('/app/chat/settings')}>
                            {t('dashboard.step5.openSettings')}
                          </Button>
                        </Box>
                      </>
                    )}
                  </BlockStack>
                </InlineStack>

                <Divider />

                {/* Step 6 (Optional) - Smart Cart Drawer */}
                <InlineStack gap="300" blockAlign="start">
                  <Box>
                    <Icon source={cartDrawerEnabled ? CheckCircleIcon : CartIcon} tone={cartDrawerEnabled ? 'success' : 'base'} />
                  </Box>
                  <BlockStack gap="200">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="p" fontWeight="semibold">{t('dashboard.step6.title', 'Step 6 (Optional): Enable Smart Cart Drawer')}</Text>
                      <Badge tone="info">{t('dashboard.step6.badge', 'New')}</Badge>
                      {cartDrawerEnabled && <Badge tone="success">{t('dashboard.step6.configured', 'Active')}</Badge>}
                    </InlineStack>
                    {cartDrawerEnabled ? (
                      <Text as="p" tone="subdued">
                        ✓ {t('dashboard.step6.description', 'A slide-out cart drawer with upsell recommendations and free shipping progress bar. Active on all pages.')}
                      </Text>
                    ) : (
                      <>
                        <Text as="p" tone="subdued">
                          {t('dashboard.step6.description', 'Add a slide-out cart drawer with upsell recommendations and free shipping progress bar. Shows on all pages when enabled.')}
                        </Text>
                        <Box paddingBlockStart="100">
                          <BlockStack gap="100">
                            <Text as="p" variant="bodySm" fontWeight="semibold">{t('dashboard.step6.howTo', 'How to enable it:')}</Text>
                            <List type="number">
                              <List.Item>{t('dashboard.step6.step1', 'Go to Settings → Smart Cart tab')}</List.Item>
                              <List.Item>{t('dashboard.step6.step2', 'Toggle "Enable Smart Cart Drawer" ON')}</List.Item>
                              <List.Item>{t('dashboard.step6.step3', 'Configure free shipping threshold and texts')}</List.Item>
                              <List.Item>{t('dashboard.step6.step4', 'Click Save')}</List.Item>
                            </List>
                          </BlockStack>
                        </Box>
                      </>
                    )}
                    <Box paddingBlockStart="200">
                      <Button onClick={() => navigate('/app/settings?tab=smartcart')}>
                        {t('dashboard.step6.openSettings', 'Open Settings')}
                      </Button>
                    </Box>
                  </BlockStack>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="200" blockAlign="center">
                  <Icon source={ChartVerticalFilledIcon} tone="base" />
                  <Text as="h3" variant="headingMd">{t('dashboard.analyticsCard.title')}</Text>
                </InlineStack>
                <Button onClick={() => navigate('/app/analytics')} icon={ViewIcon}>
                  {t('dashboard.analyticsCard.view')}
                </Button>
              </InlineStack>
              {isLoadingAnalytics ? (
                <SkeletonBodyText lines={2} />
              ) : analytics ? (
                <>
                  <Text as="p" tone="subdued">
                    {t('dashboard.analyticsCard.description')}
                  </Text>
                  <InlineStack gap="200" wrap>
                    <Badge tone="info">{`${t('dashboard.analyticsCard.views')}: ${analytics.totalProductViews?.toLocaleString() || '0'}`}</Badge>
                    <Badge tone="warning">{`${t('dashboard.analyticsCard.addToCart')}: ${analytics.totalAddToCarts?.toLocaleString() || '0'}`}</Badge>
                    <Badge tone="success">{`${t('dashboard.analyticsCard.purchases')}: ${analytics.totalPurchases?.toLocaleString() || '0'}`}</Badge>
                    <Badge tone={analytics.conversionRate >= 3 ? 'success' : analytics.conversionRate >= 1 ? 'attention' : 'critical'}>
                      {`${t('dashboard.analyticsCard.conversion')}: ${analytics.conversionRate != null ? `${analytics.conversionRate.toFixed(1)}%` : '0%'}`}
                    </Badge>
                    {attrRevenue > 0 && (
                      <Badge tone="success">{`${t('dashboard.analyticsCard.revenue', 'Revenue')}: $${attrRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</Badge>
                    )}
                    {attrAOV > 0 && (
                      <Badge tone="info">{`${t('dashboard.analyticsCard.aov', 'AOV')}: $${attrAOV.toFixed(2)}`}</Badge>
                    )}
                  </InlineStack>
                </>
              ) : (
                <Text as="p" tone="subdued">
                  {t('dashboard.analyticsCard.noData')}
                </Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="200" blockAlign="center">
                  <Icon source={EmailIcon} tone="base" />
                  <Text as="h3" variant="headingMd">{t('dashboard.settingsCard.title')}</Text>
                  {!shopInfo?.planFeatures?.hasEmailCampaigns && <Badge tone="attention">Professional</Badge>}
                </InlineStack>
                <Button onClick={() => navigate('/app/email')}>
                  {t('dashboard.settingsCard.configure')}
                </Button>
              </InlineStack>
              <Text as="p" tone="subdued">
                {t('dashboard.settingsCard.description')}
              </Text>
              <InlineStack gap="200">
                <Badge tone={emailsEnabled ? 'success' : 'attention'}>
                  {emailsEnabled ? t('dashboard.settingsCard.emailsActive') : t('dashboard.settingsCard.emailsDisabled')}
                </Badge>
                {emailsEnabled && (
                  <>
                    {settings?.emailSettings?.abandonedCartEnabled && (
                      <Badge tone="info">{t('dashboard.settingsCard.abandonedCart')}</Badge>
                    )}
                    {settings?.emailSettings?.postPurchaseEnabled && (
                      <Badge tone="info">{t('dashboard.settingsCard.postPurchase')}</Badge>
                    )}
                  </>
                )}
              </InlineStack>
              <Box paddingBlockStart="200">
                <Banner tone="info">
                  <p>{t('dashboard.settingsCard.emailProvider')}</p>
                </Banner>
              </Box>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* AI Chat Assistant */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="200" blockAlign="center">
                  <Icon source={ChatIcon} tone="base" />
                  <Text as="h3" variant="headingMd">{t('dashboard.chatCard.title')}</Text>
                  {!shopInfo?.planFeatures?.hasAiChat && <Badge tone="attention">Growth</Badge>}
                </InlineStack>
                <Button onClick={() => navigate('/app/chat/settings')}>
                  {t('dashboard.configure')}
                </Button>
              </InlineStack>
              <Text as="p" tone="subdued">
                {t('dashboard.chatCard.description')}
              </Text>
              <InlineStack gap="200">
                <Button variant="plain" onClick={() => navigate('/app/chat')}>
                  {t('dashboard.chatCard.tryChat')}
                </Button>
                <Button variant="plain" onClick={() => navigate('/app/chat/discounts')}>
                  {t('dashboard.chatCard.manageDiscounts', 'Manage Discounts')}
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Product Bundles */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="200" blockAlign="center">
                  <Icon source={ProductIcon} tone="base" />
                  <Text as="h3" variant="headingMd">{t('dashboard.bundlesCard.title')}</Text>
                  {!shopInfo?.planFeatures?.hasBundles && <Badge tone="attention">Growth</Badge>}
                </InlineStack>
                <Button onClick={() => navigate('/app/bundles')}>
                  {t('dashboard.configure')}
                </Button>
              </InlineStack>
              <Text as="p" tone="subdued">
                {t('dashboard.bundlesCard.description')}
              </Text>
              <InlineStack gap="200">
                <Button variant="plain" onClick={() => navigate('/app/bundles')}>
                  {t('bundles.manageBundles')}
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* A/B Testing */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="200" blockAlign="center">
                  <Icon source={TargetIcon} tone="base" />
                  <Text as="h3" variant="headingMd">{t('dashboard.abtestCard.title', 'A/B Testing')}</Text>
                  <Badge tone="info">{t('dashboard.abtestCard.badge', 'New')}</Badge>
                </InlineStack>
                <Button onClick={() => navigate('/app/ab-tests')}>
                  {t('dashboard.configure')}
                </Button>
              </InlineStack>
              <Text as="p" tone="subdued">
                {t('dashboard.abtestCard.description', 'Compare recommendation algorithms and measure their impact on revenue and conversions with statistical significance testing.')}
              </Text>
              <InlineStack gap="200">
                <Button variant="plain" onClick={() => navigate('/app/ab-tests')}>
                  {t('dashboard.abtestCard.manage', 'Manage Experiments')}
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Sales Strategy */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="200" blockAlign="center">
                  <Icon source={AdjustIcon} tone="base" />
                  <Text as="h3" variant="headingMd">{t('dashboard.strategyCard.title', 'Sales Strategy')}</Text>
                  <Badge tone="info">{t('dashboard.strategyCard.badge', 'New')}</Badge>
                </InlineStack>
                <Button onClick={() => navigate('/app/strategy')}>
                  {t('dashboard.configure')}
                </Button>
              </InlineStack>
              <Text as="p" tone="subdued">
                {t('dashboard.strategyCard.description', 'Control how aggressively your store engages visitors. Configure session scoring, popup intensity, and upsell behavior based on your audience.')}
              </Text>
              <InlineStack gap="200">
                <Badge tone={settings?.sessionScoring?.enabled ? 'success' : 'attention'}>
                  {settings?.sessionScoring?.enabled ? t('dashboard.strategyCard.scoringActive', 'Scoring Active') : t('dashboard.strategyCard.scoringInactive', 'Scoring Off')}
                </Badge>
                <Badge tone={settings?.popupSettings?.enabled ? 'success' : 'attention'}>
                  {settings?.popupSettings?.enabled ? t('dashboard.strategyCard.popupsActive', 'Popups Active') : t('dashboard.strategyCard.popupsInactive', 'Popups Off')}
                </Badge>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Email Recommendations */}
        {/* <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="200" blockAlign="center">
                  <Icon source={EmailIcon} tone="base" />
                  <Text as="h3" variant="headingMd">{t('dashboard.emailCard.title')}</Text>
                </InlineStack>
                <Button onClick={() => navigate('/app/email')}>
                  {t('dashboard.configure')}
                </Button>
              </InlineStack>
              <Text as="p" tone="subdued">
                {t('dashboard.emailCard.description')}
              </Text>
              <InlineStack gap="200">
                <Badge tone={emailsEnabled ? 'success' : 'attention'}>
                  {emailsEnabled ? t('dashboard.emailCard.active') : t('dashboard.emailCard.inactive')}
                </Badge>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section> */}

        {/* Spacer for oneHalf sections */}
        <Layout.Section variant="oneHalf">
          <div />
        </Layout.Section>

        {/* Recommendation Types */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h3" variant="headingMd">{t('dashboard.recommendationTypes.title', 'Recommendation Types & Features')}</Text>
              <Text as="p" tone="subdued">
                {t('dashboard.recommendationTypes.subtitle', 'All the AI-powered features available in your store')}
              </Text>
              <BlockStack gap="300">
                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <BlockStack gap="100">
                    <Text as="p" fontWeight="semibold">{t('dashboard.recommendationTypes.similar.title', 'Similar Products')}</Text>
                    <Text as="p" tone="subdued" variant="bodySm">
                      {t('dashboard.recommendationTypes.similar.description', 'Uses semantic embeddings to find visually and contextually similar products based on titles, descriptions, and tags.')}
                    </Text>
                  </BlockStack>
                </Box>
                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <BlockStack gap="100">
                    <Text as="p" fontWeight="semibold">{t('dashboard.recommendationTypes.trending.title', 'Trending Products')}</Text>
                    <Text as="p" tone="subdued" variant="bodySm">
                      {t('dashboard.recommendationTypes.trending.description', 'Surfaces products gaining popularity based on recent views, adds to cart, and purchases across your store.')}
                    </Text>
                  </BlockStack>
                </Box>
                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <BlockStack gap="100">
                    <Text as="p" fontWeight="semibold">{t('dashboard.recommendationTypes.personalized.title', 'Personalized Recommendations')}</Text>
                    <Text as="p" tone="subdued" variant="bodySm">
                      {t('dashboard.recommendationTypes.personalized.description', 'Tailors recommendations to each customer based on their browsing history, purchase patterns, and user profile.')}
                    </Text>
                  </BlockStack>
                </Box>
                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <BlockStack gap="100">
                    <Text as="p" fontWeight="semibold">{t('dashboard.recommendationTypes.bought.title', 'Frequently Bought Together')}</Text>
                    <Text as="p" tone="subdued" variant="bodySm">
                      {t('dashboard.recommendationTypes.bought.description', 'Analyzes real order data to identify products that are commonly purchased together, creating smart bundles.')}
                    </Text>
                  </BlockStack>
                </Box>
                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="p" fontWeight="semibold">{t('dashboard.recommendationTypes.smartCart.title', 'AI Smart Cart Drawer')}</Text>
                      <Badge tone="info">{t('dashboard.new', 'New')}</Badge>
                    </InlineStack>
                    <Text as="p" tone="subdued" variant="bodySm">
                      {t('dashboard.recommendationTypes.smartCart.description', 'Slide-out cart with AI upsell suggestions and a free shipping progress bar to increase average order value.')}
                    </Text>
                  </BlockStack>
                </Box>
                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="p" fontWeight="semibold">{t('dashboard.recommendationTypes.checkout.title', 'Checkout Upsell')}</Text>
                      <Badge tone="warning">{t('dashboard.recommendationTypes.checkout.plusOnly', 'Plus Only')}</Badge>
                    </InlineStack>
                    <Text as="p" tone="subdued" variant="bodySm">
                      {t('dashboard.recommendationTypes.checkout.description', 'Post-purchase upsell widget on Checkout and Thank You pages. Requires Shopify Plus for checkout extensibility.')}
                    </Text>
                  </BlockStack>
                </Box>
                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="p" fontWeight="semibold">{t('dashboard.recommendationTypes.abtest.title', 'A/B Testing')}</Text>
                      <Badge tone="info">{t('dashboard.new', 'New')}</Badge>
                    </InlineStack>
                    <Text as="p" tone="subdued" variant="bodySm">
                      {t('dashboard.recommendationTypes.abtest.description', 'Compare recommendation algorithms head-to-head with statistical significance testing. Measure real impact on conversions and revenue.')}
                    </Text>
                  </BlockStack>
                </Box>
                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="p" fontWeight="semibold">{t('dashboard.recommendationTypes.bandit.title', 'Contextual Bandit Optimization')}</Text>
                      <Badge tone="info">{t('dashboard.new', 'New')}</Badge>
                    </InlineStack>
                    <Text as="p" tone="subdued" variant="bodySm">
                      {t('dashboard.recommendationTypes.bandit.description', 'Thompson Sampling automatically finds the best algorithm for each page type. No manual tuning needed — it learns and adapts in real time.')}
                    </Text>
                  </BlockStack>
                </Box>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Pro Tips */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">{t('dashboard.proTips.title', 'Pro Tips')}</Text>
              <List>
                <List.Item>
                  {t('dashboard.proTips.attribution', 'Enable attribution tracking to measure real revenue impact — see which recommendations drive the most sales in Analytics.')}
                </List.Item>
                <List.Item>
                  {t('dashboard.proTips.abtest', 'Run A/B tests to compare algorithms before committing. Even small improvements in click-through rate can significantly boost revenue.')}
                </List.Item>
                <List.Item>
                  {t('dashboard.proTips.smartCart', 'Activate the Smart Cart Drawer with a free shipping threshold slightly above your AOV to encourage customers to add one more item.')}
                </List.Item>
                <List.Item>
                  {t('dashboard.proTips.bandit', 'The Contextual Bandit automatically optimizes which algorithm runs on each page type. Let it run for at least 2 weeks for best results.')}
                </List.Item>
                <List.Item>
                  {t('dashboard.proTips.bundles', 'Create product bundles from Frequently Bought Together data — they convert 2-3× better than random cross-sells.')}
                </List.Item>
                <List.Item>
                  {t('dashboard.proTips.chatbot', 'The AI chatbot can recommend products and apply discount codes in conversation. Enable it for a unique shopping experience.')}
                </List.Item>
              </List>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Testing & Training - Only shown for allowed domains */}
        {settings?.showTestPanel && (
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="200" blockAlign="center">
                  <Icon source={PlayIcon} tone="base" />
                  <Text as="h3" variant="headingMd">{t('dashboard.testingTraining.title', 'Testing & Training')}</Text>
                </InlineStack>
                <Button onClick={() => navigate('/app/testing')}>
                  {t('dashboard.testingTraining.fullDashboard', 'Full Dashboard')}
                </Button>
              </InlineStack>
              <Text as="p" tone="subdued">
                {t('dashboard.testingTraining.description', 'Simulate customer behavior, train AI models, analyze real orders, and verify your attribution pipeline.')}
              </Text>

              {testMessage && (
                <Banner tone="success" onDismiss={() => setTestMessage(null)}>
                  <p>{testMessage}</p>
                </Banner>
              )}

              {testError && (
                <Banner tone="critical" onDismiss={() => setTestError(null)}>
                  <p>{testError}</p>
                </Banner>
              )}

              <BlockStack gap="400">
                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                      <Text as="p" fontWeight="semibold">{t('dashboard.testingTraining.simulate.title', 'Simulate Behavior')}</Text>
                      <Text as="p" tone="subdued" variant="bodySm">
                        {t('dashboard.testingTraining.simulate.description', 'Generate synthetic browsing and purchase events to test the recommendation pipeline end-to-end.')}
                      </Text>
                    </BlockStack>
                    <Button
                      onClick={handleSimulateBehavior}
                      loading={isSimulating}
                      disabled={isSimulating || isTraining || isAnalyzingOrders}
                    >
                      {t('dashboard.testingTraining.simulate.button', 'Simulate')}
                    </Button>
                  </InlineStack>
                </Box>

                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                      <Text as="p" fontWeight="semibold">{t('dashboard.testingTraining.train.title', 'Train Model')}</Text>
                      <Text as="p" tone="subdued" variant="bodySm">
                        {t('dashboard.testingTraining.train.description', 'Retrain the recommendation model using the latest behavior data and product embeddings.')}
                      </Text>
                    </BlockStack>
                    <Button
                      onClick={handleTrainModel}
                      loading={isTraining}
                      disabled={isSimulating || isTraining || isAnalyzingOrders}
                    >
                      {t('dashboard.testingTraining.train.button', 'Train')}
                    </Button>
                  </InlineStack>
                </Box>

                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                      <Text as="p" fontWeight="semibold">{t('dashboard.testingTraining.analyze.title', 'Analyze Orders')}</Text>
                      <Text as="p" tone="subdued" variant="bodySm">
                        {t('dashboard.testingTraining.analyze.description', 'Process real order history to build co-purchase associations and update Frequently Bought Together data.')}
                      </Text>
                    </BlockStack>
                    <Button
                      onClick={handleAnalyzeOrders}
                      loading={isAnalyzingOrders}
                      disabled={isSimulating || isTraining || isAnalyzingOrders}
                    >
                      {t('dashboard.testingTraining.analyze.button', 'Analyze')}
                    </Button>
                  </InlineStack>
                </Box>

                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="p" fontWeight="semibold">{t('dashboard.testingTraining.attribution.title', 'Verify Attribution')}</Text>
                        <Badge tone="info">{t('dashboard.new', 'New')}</Badge>
                      </InlineStack>
                      <Text as="p" tone="subdued" variant="bodySm">
                        {t('dashboard.testingTraining.attribution.description', 'Check that recommendation clicks are being tracked and attributed to conversions. View the revenue pipeline in Analytics.')}
                      </Text>
                    </BlockStack>
                    <Button onClick={() => navigate('/app/analytics')}>
                      {t('dashboard.testingTraining.attribution.button', 'View Analytics')}
                    </Button>
                  </InlineStack>
                </Box>

                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="p" fontWeight="semibold">{t('dashboard.testingTraining.abquick.title', 'Quick A/B Test')}</Text>
                        <Badge tone="info">{t('dashboard.new', 'New')}</Badge>
                      </InlineStack>
                      <Text as="p" tone="subdued" variant="bodySm">
                        {t('dashboard.testingTraining.abquick.description', 'Launch a quick experiment to compare two algorithms. Results will show statistical significance once enough data is collected.')}
                      </Text>
                    </BlockStack>
                    <Button onClick={() => navigate('/app/ab-tests')}>
                      {t('dashboard.testingTraining.abquick.button', 'Create Test')}
                    </Button>
                  </InlineStack>
                </Box>
              </BlockStack>

              {hasBehaviorData && (
                <Box paddingBlockStart="200">
                  <InlineStack gap="400">
                    <Badge tone="success">{t('dashboard.testingTraining.eventsTracked', { count: String(setupStatus?.totalBehaviors?.toLocaleString() || 0) } as Record<string, unknown>)}</Badge>
                    {hasAssociations && (
                      <Badge tone="info">{t('dashboard.testingTraining.productAssociations', { count: setupStatus?.totalAssociations || 0 })}</Badge>
                    )}
                  </InlineStack>
                </Box>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
        )}

        {/* Preview */}
        <Layout.Section>
          <Card>
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="100">
                <Text as="h3" variant="headingMd">{t('dashboard.preview.title')}</Text>
                <Text as="p" tone="subdued">{t('dashboard.preview.description')}</Text>
              </BlockStack>
              <Button url={`https://${shop}/collections/all`} external variant="primary">
                {t('dashboard.preview.viewStore')}
              </Button>
            </InlineStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
